import { usagePercent, usageState } from "../lib/usage-org";
import styles from "./UsageBar.module.css";

export interface UsageBarProps {
  used: number;
  quota: number | null;
  collapsed: boolean;
  /** Name der Organisation, für die gezählt wird */
  orgName?: string;
  /** true, wenn der Betrachter dieser Organisation nicht angehört (Plattform-Admin im Kundenkanal) */
  foreign?: boolean;
}

/**
 * Verbrauchsanzeige „Vorgänge" für die Seitenleiste (KOL-94, Prozent seit KOL-132).
 *
 * Zeigt die Auslastung der ganzen Organisation über alle Bots, nicht die des
 * einzelnen Nutzers. Über dem Kontingent wird weitergearbeitet (Entscheidung
 * 19.08.), der Balken bleibt dann bei 100 % stehen — die Prozentzahl daneben
 * läuft weiter, damit niemand den Mehrverbrauch übersieht.
 *
 * Wann überhaupt angezeigt wird, entscheidet `zeigeVerbrauch()` beim Aufrufer:
 * Die Komponente stellt dar, sie hält nichts zurück.
 */
export function UsageBar({ used, quota, collapsed, orgName, foreign }: UsageBarProps) {
  const state = usageState(used, quota);
  const prozent = usagePercent(used, quota);
  // Bei 100 % ist Schluss, für Balken UND Zahl. Über dem Kontingent wird
  // weitergearbeitet (Entscheidung 19.08.), aber „150 %" liest sich wie ein
  // Defekt statt wie ein Hinweis. Wie weit darüber, beantwortet das
  // Admin-Panel — dort stehen die echten Zahlen je Organisation.
  const fill = prozent === null ? 0 : Math.min(100, prozent);

  // Prozent statt absoluter Zahlen (KOL-132): Ob 1.880 Vorgänge viel sind,
  // weiß nur, wer das Kontingent auswendig kennt — „94 %" weiß es sofort.
  const anzeige = prozent === null ? "" : `${fill} % des Kontingents`;
  // Die absoluten Zahlen bleiben im Titel: für die Anzeige zu sperrig, für
  // eine Rückfrage („wie viele sind das?") zu nützlich zum Wegwerfen. Sie
  // sind ungedeckelt — wer nachsieht, soll den Mehrverbrauch auch finden.
  const genau =
    quota !== null
      ? `${used.toLocaleString("de-DE")} von ${quota.toLocaleString("de-DE")} Bot-Vorgängen`
      : "";

  // Der Name steht nur bei fremdem Verbrauch dabei: Im Kundenkanal sieht man
  // als Plattform-Admin dessen Zahlen und darf sie nicht für die eigenen
  // halten. In der eigenen Organisation wäre der Name bloß Rauschen.
  const fremd = Boolean(foreign && orgName);
  const kern = genau ? `${anzeige} (${genau})` : anzeige;
  const label = fremd ? `${orgName}: ${kern}` : kern;

  return (
    <div className={`${styles.wrap} ${collapsed ? styles.collapsed : ""}`}>
      <div
        role="progressbar"
        aria-valuenow={fill}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
        title={label}
        className={`${styles.track} ${styles[state]}`}
      >
        <div className={styles.fill} style={{ width: `${fill}%` }} />
      </div>
      {!collapsed && (
        <span className={`${styles.label} ${fremd ? styles.fremd : ""}`}>
          {fremd && <span className={styles.orgName}>{orgName}</span>}
          {fremd ? " · " : ""}
          {anzeige}
        </span>
      )}
    </div>
  );
}
